import { useCallback, useEffect, useState } from 'react';

import { useSegmentAnalyticsContext } from '@/SegmentAnalyticsProvider';
import { useDatadogContext } from '@/context/hooks/useDatadogContext';
import { useStoredOrganizationId } from '@/hooks/useStoredOrganizationId';
import {
  useCreateOrganization,
  useOrganizations,
  useTenantList,
} from '@/hooks/useSwr';
import { OrganizationBaseSchema } from '@/types/schema';
import { fireSignupConversionEvent as fireGoogleTagManager } from '@/utils/google-tag-manager';

import { OnboardingStepV2, signupConversionFiredKey } from '../constants';

export const useCreateOrgOnLoad = ({
  waitingForOrgCreation,
  setWaitingForOrgCreation,
  setSearchParams,
}: {
  waitingForOrgCreation: boolean;
  setWaitingForOrgCreation: (waitingForOrgCreation: boolean) => void;
  setSearchParams: (params: (prev: URLSearchParams) => URLSearchParams) => void;
}) => {
  const { datadogLogs } = useDatadogContext();
  const [hasCreatedOrg, setHasCreatedOrg] = useState(false);
  const { trigger: createOrg, isMutating, error } = useCreateOrganization();
  const { setOrgId } = useStoredOrganizationId();
  const { data: tenantList } = useTenantList();
  const newTenant = tenantList?.[0];
  const newTenantId = newTenant?.id;
  const { mutate } = useOrganizations();

  const { analytics, data } = useSegmentAnalyticsContext();
  const fireSignup = useCallback(() => {
    if (data == null || analytics == null) return;

    const hasSignupConversionFired = localStorage.getItem(
      signupConversionFiredKey(data.user?.supabase_user_id)
    );

    if (!hasSignupConversionFired) {
      analytics.track('Signed Up');
      fireGoogleTagManager();
      localStorage.setItem(
        signupConversionFiredKey(data.user?.supabase_user_id),
        'true'
      );
    }
  }, [analytics, data]);

  useEffect(() => {
    const createOrgCallback = async () => {
      try {
        const newOrg = await createOrg({
          json: {
            display_name: 'Personal',
            is_personal: true,
          },
        });
        if (newOrg) {
          setOrgId(newOrg.id);
          fireSignup();
          setHasCreatedOrg(true);
        }
      } catch (error: unknown) {
        // Handle 409 Conflict - organization already exists
        if (
          error instanceof Error &&
          'status' in error &&
          error.status === 409
        ) {
          // Fetch organizations to find the existing personal one.
          // We lock on the backend, so it must exist at this point.
          const updatedOrgs = await mutate();

          if (updatedOrgs && Array.isArray(updatedOrgs)) {
            const personalOrg = updatedOrgs.find(
              (org: OrganizationBaseSchema) => org.is_personal
            );
            if (personalOrg) {
              // we don't fire signup here because we presumably already fired it when the org was created concurrently
              setOrgId(personalOrg.id);
              setHasCreatedOrg(true);
            } else {
              datadogLogs?.logger?.warn(
                'Failed to find personal organization after create conflict.',
                { updatedOrgs }
              );
            }
          } else {
            datadogLogs?.logger?.warn('Failed to find any organizations.');
          }
        } else {
          if (error instanceof Error) {
            throw error;
          } else {
            throw new Error('Failed to create organization');
          }
        }
      }
    };
    // only create the org once the tenant list is loaded
    if (!hasCreatedOrg && tenantList && !isMutating) {
      createOrgCallback();
    }
    // we should not create the org again if fireSignup  or setOrgId change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOrg, tenantList, hasCreatedOrg, setHasCreatedOrg]);

  useEffect(() => {
    if (newTenantId && hasCreatedOrg) {
      setSearchParams((prev) => {
        prev.set('organizationId', newTenantId);
        if (waitingForOrgCreation) {
          prev.set('step', OnboardingStepV2.Options.toString());
          setWaitingForOrgCreation(false);
        }
        return prev;
      });
    }
  }, [
    newTenantId,
    hasCreatedOrg,
    setSearchParams,
    setWaitingForOrgCreation,
    waitingForOrgCreation,
  ]);

  return { hasCreatedOrg, isCreatingOrg: isMutating, createOrgError: error };
};
